/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import type { Tables } from "@app/@classes/supabase";
import { BugReportsService } from "@app/@services/data/bug-reports.service";
import { ActionLoggerService } from "@app/@services/generic/action-logger.service";
import { AuthStateService } from "@app/@services/shared/auth-state.service";
import { ButtonComponent } from "@app/widgets/base/button/button.component";
import { DialogLayoutComponent } from "@app/widgets/base/dialog-layout/dialog-layout.component";
import { CbDialogRef } from "@app/widgets/base/dialog/dialog.class";
import { CbFormGroup, CbValidator } from "@app/widgets/base/form/form-group";
import { FormModule } from "@app/widgets/base/form/form.module";
import type { CbFormSubmitEvent } from "@app/widgets/base/form/form/form.component";
import { TranslateModule } from "@ngx-translate/core";
import { catchError, of } from "rxjs";
import { environment } from "src/environments/environment";

const TABLE = "bug_reports";
type Entity = Tables<typeof TABLE>;

@Component({
	selector: "cb-bug-report-dialog",

	imports: [
		DialogLayoutComponent,
		FormModule,
		TranslateModule,
		ButtonComponent,
	],
	templateUrl: "./bug-report-dialog.component.html",
	styleUrl: "./bug-report-dialog.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BugReportDialogComponent {
	private readonly bugReportsService: BugReportsService =
		inject(BugReportsService);
	private readonly actionLogger: ActionLoggerService =
		inject(ActionLoggerService);
	private readonly dialogRef: CbDialogRef = inject(CbDialogRef);
	private readonly authStateService: AuthStateService = inject(AuthStateService);

	protected formGroup: CbFormGroup = new CbFormGroup({
		notes: {
			initialValue: "",
			required: true,
			validators: [CbValidator.minLength(10)],
		},
		attachments: {
			initialValue: [],
			required: false,
		},
		logs: {
			initialValue: [],
			required: false,
		},
	});

	public get title(): string {
		return "BUG_REPORT.TITLE";
	}

	saveAndClose(event: CbFormSubmitEvent): void {
		const item = {
			...this.bugReportsService.defaultItem,
			...(this.formGroup.valuesObject as {
				notes: string;
				logs: string;
				attachments: string[];
			}),
			user_id: this.authStateService.currentUserId() ?? "",
			tenant_id: this.authStateService.currentTenantId() ?? "",
			logs: JSON.parse(JSON.stringify(this.actionLogger.logs)),
			project_name: environment.projectName,
		};

		this.bugReportsService
			.sendBugReport(item)
			.pipe(
				catchError((error) => {
					event.error.next(error);
					return of(null);
				}),
			)
			.subscribe(() => {
				event.success.next();
				this.dialogRef.close();
			});
	}
}
